"use client";

/**
 * Org-wide brand scheme (William 2026-08-12).
 *   classic  = legacy amber CTAs (Brand A)
 *   valencia = design-language red #BF311A / yellow #FDB913 (Brand B)
 *
 * Source of truth: app_settings.brand_scheme (Admin Settings).
 * localStorage `ga-brand-ab` is a paint cache only (a|b ↔ classic|valencia).
 * Layout / nav unchanged — colors via data-brand + CSS tokens.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getBrandScheme, setBrandScheme as persistBrandScheme, type BrandScheme } from '@/actions/brand';

export type BrandAb = 'a' | 'b';

const STORAGE_KEY = 'ga-brand-ab';

function schemeToAb(scheme: BrandScheme): BrandAb {
  return scheme === 'classic' ? 'a' : 'b';
}

function abToScheme(ab: BrandAb): BrandScheme {
  return ab === 'a' ? 'classic' : 'valencia';
}

type BrandAbContextValue = {
  brand: BrandAb;
  scheme: BrandScheme;
  setBrand: (b: BrandAb) => void;
  setScheme: (s: BrandScheme) => Promise<void>;
  toggleBrand: () => void;
  ready: boolean;
};

const BrandAbContext = createContext<BrandAbContextValue | null>(null);

function applyBrandAttr(brand: BrandAb) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-brand', brand);
}

function readCache(): BrandAb {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'a' || saved === 'b') return saved;
  } catch { /* ignore */ }
  return 'b'; // migrated default: red/yellow
}

function writeCache(brand: BrandAb) {
  try {
    localStorage.setItem(STORAGE_KEY, brand);
  } catch { /* ignore */ }
}

export function BrandAbProvider({ children }: { children: React.ReactNode }) {
  const [brand, setBrandState] = useState<BrandAb>('b');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const cached = readCache();
    setBrandState(cached);
    applyBrandAttr(cached);

    let cancelled = false;
    (async () => {
      try {
        const scheme = await getBrandScheme();
        if (cancelled) return;
        const next = schemeToAb(scheme);
        setBrandState(next);
        applyBrandAttr(next);
        writeCache(next);
      } catch {
        // Keep cache / red-yellow default if settings unreadable
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const setBrand = useCallback((b: BrandAb) => {
    setBrandState(b);
    applyBrandAttr(b);
    writeCache(b);
    void persistBrandScheme(abToScheme(b)).catch(() => { /* admin may lack write */ });
  }, []);

  const setScheme = useCallback(async (s: BrandScheme) => {
    const b = schemeToAb(s);
    setBrandState(b);
    applyBrandAttr(b);
    writeCache(b);
    await persistBrandScheme(s);
  }, []);

  const toggleBrand = useCallback(() => {
    setBrand(brand === 'a' ? 'b' : 'a');
  }, [brand, setBrand]);

  const value: BrandAbContextValue = {
    brand,
    scheme: abToScheme(brand),
    setBrand,
    setScheme,
    toggleBrand,
    ready,
  };

  return (
    <BrandAbContext.Provider value={value}>
      {children}
    </BrandAbContext.Provider>
  );
}

export function useBrandAb() {
  const ctx = useContext(BrandAbContext);
  if (!ctx) {
    return {
      brand: 'b' as BrandAb,
      scheme: 'valencia' as BrandScheme,
      setBrand: (_b: BrandAb) => {},
      setScheme: async (_s: BrandScheme) => {},
      toggleBrand: () => {},
      ready: false,
    };
  }
  return ctx;
}
